import React, { useEffect, useState } from 'react';
import { getWarehouses } from '../../services/warehousesService';
import { getPointOfSale } from '../../services/pointOfSalesService';

const currentLimaTimeHm = () => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'America/Lima',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const hour = parts.find((p) => p.type === 'hour')?.value || '00';
    const minute = parts.find((p) => p.type === 'minute')?.value || '00';
    return `${hour}:${minute}`;
  } catch {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  }
};

const VentasCloseShiftModal = ({
  isOpen,
  dayGroup,
  pointOfSale,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [exitTime, setExitTime] = useState(currentLimaTimeHm);
  const [notes, setNotes] = useState('');
  const [defaultWarehouseId, setDefaultWarehouseId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [warehousesRes, posRes] = await Promise.all([
          getWarehouses({ active_only: true }),
          pointOfSale?.id && !pointOfSale?.default_warehouse_id
            ? getPointOfSale(pointOfSale.id)
            : Promise.resolve(null),
        ]);
        if (!cancelled && warehousesRes.success) {
          setWarehouses(warehousesRes.data || []);
        }
        if (!cancelled) {
          const fromPos =
            pointOfSale?.default_warehouse_id ||
            (posRes?.success ? posRes.data?.default_warehouse_id : null);
          setDefaultWarehouseId(fromPos || null);
        }
      } catch {
        if (!cancelled) {
          setWarehouses([]);
          setDefaultWarehouseId(pointOfSale?.default_warehouse_id || null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, pointOfSale?.id, pointOfSale?.default_warehouse_id]);

  useEffect(() => {
    if (!isOpen) {
      setWarehouseId('');
      setExitTime(currentLimaTimeHm());
      setNotes('');
      setDefaultWarehouseId(null);
      return;
    }
    setExitTime(currentLimaTimeHm());
    if (defaultWarehouseId) {
      setWarehouseId(String(defaultWarehouseId));
    }
  }, [isOpen, dayGroup?.key, defaultWarehouseId]);

  if (!isOpen || !dayGroup) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!warehouseId || !exitTime) return;
    onConfirm({
      shift_date: dayGroup.date || dayGroup.key,
      warehouse_id: Number(warehouseId),
      exit_time: exitTime,
      notes: notes.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Cerrar turno</h2>
            <p className="text-sm text-gray-600 mt-1">
              {dayGroup.label}
              {pointOfSale?.name ? ` · ${pointOfSale.name}` : ''}
            </p>
          </div>

          <div className="px-6 py-4 space-y-4">
            <p className="text-sm text-gray-600">
              Se generará una guía de salida por cada venta con productos y el turno quedará cerrado.
              Después no se podrán registrar ni modificar ventas de esta fecha.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Almacén de salida <span className="text-red-500">*</span>
              </label>
              <select
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
              >
                <option value="">Seleccione almacén...</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={String(warehouse.id)}>
                    {warehouse.code ? `${warehouse.code} — ${warehouse.name}` : warehouse.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora de salidas <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={exitTime}
                onChange={(e) => setExitTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Esta hora se registrará en las guías de salida del turno.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Opcional"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !warehouseId || !exitTime}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md disabled:opacity-60"
            >
              {loading ? 'Cerrando...' : 'Ejecutar salidas y cerrar turno'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VentasCloseShiftModal;
